import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { almacen, newOperationId } from '../api';
import { decimasDeGrado, fueraDeRango, grados, type EquipoDeFrio } from '../botica';
import {
  Boton,
  Cabecera,
  Cargando,
  Fallo,
  Modal,
  Pastilla,
  Rotulo,
  Tarjeta,
  Vacio,
} from '../componentes';
import { dia, diaDeLima, hora, sumarDias } from '../formato';

/**
 * La cadena de frío: cada refrigeradora con su rango y sus lecturas.
 *
 * La insulina y las vacunas viven entre 2 y 8 °C, y la DIGEMID pide el
 * registro de temperatura de cada equipo. Se apuntaba en un cuaderno pegado a
 * la puerta que nadie miraba hasta la inspección. Aquí cada lectura queda con
 * quién la tomó y cuándo, y la que se sale del rango se dice en rojo al
 * guardarla —que es cuando todavía se puede mover lo de adentro—. Ver docs/15
 * §2.53 (migración 273).
 */
export function CadenaDeFrio() {
  const clientes = useQueryClient();
  const equipos = useQuery({ queryKey: ['cadena-de-frio'], queryFn: almacen.coldStorage });
  const [editando, setEditando] = useState<EquipoDeFrio | 'nuevo' | null>(null);
  const [viendo, setViendo] = useState<string | null>(null);

  const lista = equipos.data ?? [];

  return (
    <>
      <Cabecera
        titulo="Cadena de frío"
        subtitulo="La temperatura de cada refrigeradora, lectura por lectura"
        ayuda="Cada equipo de frío con el rango en que tiene que estar. Anota la temperatura cada vez que la mires —al abrir, a mediodía, al cerrar—: queda con tu nombre y la hora, y si se salió del rango se avisa al momento."
        acciones={
          <Boton variante="primario" onClick={() => setEditando('nuevo')}>
            Agregar equipo
          </Boton>
        }
      />

      <div className="cuerpo">
        {equipos.isError ? (
          <Fallo error={equipos.error} onReintentar={() => equipos.refetch()} />
        ) : null}

        {equipos.isLoading ? (
          <Cargando que="los equipos de frío" />
        ) : lista.length === 0 ? (
          <Vacio
            titulo="Ningún equipo de frío"
            mensaje="Agrega cada refrigeradora o congeladora con su rango —2 a 8 °C para vacunas e insulina— y empieza a anotar sus lecturas."
          />
        ) : (
          <div className="dos-columnas">
            {lista.map((equipo) => (
              <TarjetaDeEquipo
                key={equipo.id}
                equipo={equipo}
                onEditar={() => setEditando(equipo)}
                onVer={() => setViendo(viendo === equipo.id ? null : equipo.id)}
                abierto={viendo === equipo.id}
                onAnotada={() => void clientes.invalidateQueries({ queryKey: ['cadena-de-frio'] })}
              />
            ))}
          </div>
        )}
      </div>

      {editando === null ? null : (
        <EditarEquipo
          equipo={editando === 'nuevo' ? null : editando}
          onCerrar={() => setEditando(null)}
          onGuardado={() => {
            setEditando(null);
            void clientes.invalidateQueries({ queryKey: ['cadena-de-frio'] });
          }}
        />
      )}
    </>
  );
}

function TarjetaDeEquipo({
  equipo,
  onEditar,
  onVer,
  abierto,
  onAnotada,
}: {
  equipo: EquipoDeFrio;
  onEditar: () => void;
  onVer: () => void;
  abierto: boolean;
  onAnotada: () => void;
}) {
  const clientes = useQueryClient();
  const [texto, setTexto] = useState('');
  const [nota, setNota] = useState('');
  const [lecturaId, setLecturaId] = useState(newOperationId);
  const decimas = decimasDeGrado(texto);

  const anotar = useMutation({
    mutationFn: () =>
      almacen.recordColdReading(equipo.id, {
        // El mismo id en un reintento: la lectura no se anota dos veces.
        id: lecturaId,
        celsiusTenths: decimas!,
        note: nota.trim() === '' ? null : nota.trim(),
      }),
    onSuccess: () => {
      setLecturaId(newOperationId());
      setTexto('');
      setNota('');
      onAnotada();
      void clientes.invalidateQueries({ queryKey: ['lecturas-de-frio', equipo.id] });
    },
  });

  const hoy = diaDeLima();
  const lecturas = useQuery({
    queryKey: ['lecturas-de-frio', equipo.id],
    queryFn: () => almacen.coldReadings(equipo.id, sumarDias(hoy, -6), hoy),
    enabled: abierto,
  });

  return (
    <Tarjeta>
      <h2 className="tarjeta__titulo">
        {equipo.name}{' '}
        <Pastilla texto={`${grados(equipo.minTenths)} a ${grados(equipo.maxTenths)}`} tono="neutro" />
      </h2>

      {equipo.lastReadAt === null || equipo.lastTenths === null ? (
        <p className="nota nota--pegada">Todavía sin lecturas.</p>
      ) : (
        <p
          className={
            equipo.lastOutOfRange === true ? 'aviso-caja aviso-caja--peligro' : 'nota nota--pegada'
          }
          role={equipo.lastOutOfRange === true ? 'alert' : undefined}
        >
          Última: <strong className="mono">{grados(equipo.lastTenths)}</strong> el{' '}
          {dia(equipo.lastReadAt)} a las {hora(equipo.lastReadAt)}
          {equipo.lastOutOfRange === true ? ': fuera de rango. Revisa lo que hay adentro.' : '.'}
        </p>
      )}
      {equipo.outOfRangeLast7Days > 0 ? (
        <p className="nota-sm nota--pegada">
          {equipo.outOfRangeLast7Days === 1
            ? '1 lectura fuera de rango en los últimos siete días.'
            : `${equipo.outOfRangeLast7Days} lecturas fuera de rango en los últimos siete días.`}
        </p>
      ) : null}

      <div className="campo">
        <Rotulo
          htmlFor={`temperatura-${equipo.id}`}
          ayuda="Lo que marca el termómetro ahora, en grados: 4,5 o -18."
        >
          Temperatura ahora
        </Rotulo>
        <input
          id={`temperatura-${equipo.id}`}
          inputMode="decimal"
          placeholder="4,5"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        {texto.trim() !== '' && decimas === null ? (
          <p className="nota-sm nota--pegada">Escríbela en grados, con un decimal: 4,5.</p>
        ) : decimas !== null && fueraDeRango(decimas, equipo) ? (
          <p className="aviso-caja aviso-caja--peligro">
            {grados(decimas)} está fuera del rango de este equipo. Se anota igual: es justo lo que
            tiene que quedar.
          </p>
        ) : null}
      </div>
      <div className="campo">
        <Rotulo htmlFor={`nota-${equipo.id}`} ayuda="Qué pasó, si se salió: «puerta abierta», «corte de luz».">
          Nota
        </Rotulo>
        <input
          id={`nota-${equipo.id}`}
          maxLength={240}
          value={nota}
          onChange={(e) => setNota(e.target.value)}
        />
      </div>

      {anotar.isError ? <Fallo error={anotar.error} /> : null}
      {anotar.isSuccess ? (
        <p className={anotar.data.outOfRange ? 'aviso-caja aviso-caja--peligro' : 'aviso-caja'}>
          {anotar.data.outOfRange
            ? 'Anotada fuera de rango. Mueve lo de adentro si hace falta y avisa a quien responde.'
            : 'Anotada.'}
        </p>
      ) : null}

      <div className="acciones">
        <Boton
          variante="primario"
          onClick={() => anotar.mutate()}
          disabled={decimas === null || anotar.isPending}
        >
          {anotar.isPending ? 'Anotando…' : 'Anotar lectura'}
        </Boton>
        <Boton onClick={onVer}>{abierto ? 'Ocultar la semana' : 'Ver la semana'}</Boton>
        <Boton onClick={onEditar}>Cambiar rango</Boton>
      </div>

      {!abierto ? null : lecturas.isLoading ? (
        <Cargando que="las lecturas" />
      ) : lecturas.isError ? (
        <Fallo error={lecturas.error} onReintentar={() => lecturas.refetch()} />
      ) : (lecturas.data ?? []).length === 0 ? (
        <p className="nota nota--pegada">Ninguna lectura en los últimos siete días.</p>
      ) : (
        <div className="tabla-envoltura">
          <table>
            <thead>
              <tr>
                <th>Cuándo</th>
                <th className="numero">Temperatura</th>
                <th>Quién</th>
                <th>Nota</th>
              </tr>
            </thead>
            <tbody>
              {(lecturas.data ?? []).map((lectura) => (
                <tr key={lectura.id}>
                  <td className="apagado mono">
                    {dia(lectura.readAt)} {hora(lectura.readAt)}
                  </td>
                  <td className="numero">
                    {grados(lectura.celsiusTenths)}
                    {lectura.wasOutOfRange ? <Pastilla texto="fuera de rango" tono="peligro" /> : null}
                  </td>
                  <td className="apagado">{lectura.recordedBy}</td>
                  <td className="apagado">{lectura.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Tarjeta>
  );
}

function EditarEquipo({
  equipo,
  onCerrar,
  onGuardado,
}: {
  equipo: EquipoDeFrio | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const [id] = useState(() => equipo?.id ?? newOperationId());
  const [nombre, setNombre] = useState(equipo?.name ?? '');
  // Lo de las vacunas y la insulina, que es lo que casi siempre se guarda.
  const [minimo, setMinimo] = useState(equipo === null ? '2' : String(equipo.minTenths / 10).replace('.', ','));
  const [maximo, setMaximo] = useState(equipo === null ? '8' : String(equipo.maxTenths / 10).replace('.', ','));

  const min = decimasDeGrado(minimo);
  const max = decimasDeGrado(maximo);
  const valido = nombre.trim() !== '' && min !== null && max !== null && min < max;

  const guardar = useMutation({
    mutationFn: () =>
      almacen.saveColdStorageUnit({ id, name: nombre.trim(), minTenths: min!, maxTenths: max! }),
    onSuccess: onGuardado,
  });

  return (
    <Modal titulo={equipo === null ? 'Agregar equipo de frío' : `Cambiar ${equipo.name}`} onCerrar={onCerrar}>
      <div className="formulario">
        <div className="campo">
          <Rotulo htmlFor="frio-nombre" ayuda="Como lo conoce el local: «Refrigeradora de vacunas».">
            Nombre
          </Rotulo>
          <input id="frio-nombre" maxLength={80} value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </div>
        <div className="campo">
          <Rotulo htmlFor="frio-min" ayuda="La temperatura más baja permitida, en grados.">
            Mínima
          </Rotulo>
          <input id="frio-min" inputMode="decimal" value={minimo} onChange={(e) => setMinimo(e.target.value)} />
        </div>
        <div className="campo">
          <Rotulo htmlFor="frio-max" ayuda="La temperatura más alta permitida, en grados.">
            Máxima
          </Rotulo>
          <input id="frio-max" inputMode="decimal" value={maximo} onChange={(e) => setMaximo(e.target.value)} />
        </div>
        {min !== null && max !== null && min >= max ? (
          <p className="nota-sm nota--pegada">La mínima tiene que ser menor que la máxima.</p>
        ) : null}
        {equipo === null ? null : (
          <p className="nota-sm nota--pegada">
            Las lecturas de antes siguen diciendo lo que dijeron con el rango de entonces.
          </p>
        )}
        {guardar.isError ? <Fallo error={guardar.error} /> : null}
        <Boton variante="primario" onClick={() => guardar.mutate()} disabled={!valido || guardar.isPending}>
          {guardar.isPending ? 'Guardando…' : 'Guardar'}
        </Boton>
      </div>
    </Modal>
  );
}
